import { useEffect } from 'react';
import { useSearchParams } from 'react-router';

import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';

import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';

const TEAM_MODES = ['ALL', 'CLUB', 'NATIONAL_TEAM'] as const;
export type TeamMode = (typeof TEAM_MODES)[number];

export const TEAM_MODE_URL_KEY = 'tm';

const parseTeamMode = (raw: string | null | undefined): TeamMode => {
  if (raw && (TEAM_MODES as readonly string[]).includes(raw)) {
    return raw as TeamMode;
  }
  return 'ALL';
};

export const useTeamMode = (): TeamMode => {
  const [searchParams] = useSearchParams();
  return parseTeamMode(searchParams.get(TEAM_MODE_URL_KEY));
};

/**
 * Tab-indicator team mode. The un-slugged route has no CLUB tab, so a
 * `?tm=CLUB` override there still highlights the `All` tab even though the
 * backend scopes stats to club. URL drives data, this drives the indicator.
 */
export const useDerivedTeamMode = (
  hasSo5CompetitionSlug: boolean
): TeamMode => {
  const teamMode = useTeamMode();
  if (hasSo5CompetitionSlug) return 'CLUB';
  return teamMode === 'NATIONAL_TEAM' ? 'NATIONAL_TEAM' : 'ALL';
};

/**
 * Syncs `?tm=` to the active competition tab + Advanced view override.
 * `enabled=false` forces ALL (used to gate behind a feature flag).
 */
export const useSyncTeamModeUrl = ({
  hasSo5CompetitionSlug,
  enabled,
}: {
  hasSo5CompetitionSlug: boolean;
  enabled: boolean;
}) => {
  const [, setSearchParams] = useSearchParams();
  const { statsManager } = useManagePlayerSearchView();
  const override =
    statsManager[PlayerSearchViewAttributes.TEAM_MODE_OVERRIDE] ?? 'AUTO';
  const currentTm = useTeamMode();
  const desiredTm: TeamMode = !enabled
    ? 'ALL'
    : override !== 'AUTO'
      ? override
      : hasSo5CompetitionSlug
        ? 'CLUB'
        : currentTm;

  useEffect(() => {
    if (desiredTm === currentTm) return;
    setSearchParams(
      prev => {
        if (desiredTm === 'ALL') prev.delete(TEAM_MODE_URL_KEY);
        else prev.set(TEAM_MODE_URL_KEY, desiredTm);
        return prev;
      },
      { replace: true }
    );
  }, [desiredTm, currentTm, setSearchParams]);
};
